/**
 * Who is signed in and what they may do (GET /api/v1/me, decisions-log W8–W10). The root route
 * loads it once; screens ask `useCan()`. A server without /me (or a test harness that does not
 * answer it) is the local mode: the implicit owner.
 */
import type { MeResponse } from '@outsource/shared/auth/me';
import { PERMISSIONS, type Permission } from '@outsource/shared/auth/permissions';
import { queryOptions, useQuery } from '@tanstack/react-query';
import { api, isApiClientError } from './api';

export const LOCAL_ME: MeResponse = {
  mode: 'local',
  user: null,
  mfa: 'off',
  isOwner: true,
  // Local mode has no users to manage (W1).
  permissions: PERMISSIONS.filter((permission) => permission !== 'users.manage'),
  scope: { isAll: true, issuerIds: [], projectIds: [] },
};

const HTTP_UNAUTHORIZED = 401;
const HTTP_NOT_FOUND = 404;
const ME_STALE_MS = 60_000;

async function fetchMe(): Promise<MeResponse | null> {
  try {
    return await api.get<MeResponse>('/me');
  } catch (error) {
    if (isApiClientError(error) && error.status === HTTP_UNAUTHORIZED) return null;
    if (isApiClientError(error) && error.status === HTTP_NOT_FOUND) return LOCAL_ME;
    throw error;
  }
}

/** null = signed out (online). */
export const meQuery = queryOptions({ queryKey: ['me'] as const, queryFn: fetchMe, staleTime: ME_STALE_MS });

export function useMe(): MeResponse {
  return useQuery(meQuery).data ?? LOCAL_ME;
}

export type Need = Permission | 'signed-in';

export function canDo(me: MeResponse, need: Need): boolean {
  return need === 'signed-in' || me.permissions.includes(need);
}

export function useCan(): (need: Need) => boolean {
  const me = useMe();
  return (need) => canDo(me, need);
}
